import React from 'react';
import { motion } from 'framer-motion';
import { Package, MapPin, Phone, MessageSquare, Truck, CheckCircle2, Clock, Map } from 'lucide-react';
import { useNavigation } from '../context/NavigationContext';
import { useApp } from '../context/AppContext';
import { Screen } from '../types';
import { Header } from '../components/ui/Header';
import { Button } from '../components/ui/Button';
export function OrderTrackingScreen() {
  const {
    currentParams,
    navigate
  } = useNavigation();
  const {
    orders
  } = useApp();
  const orderId = currentParams?.orderId;
  const order = orders.find(o => o.id === orderId) || orders[0];
  if (!order) return null;
  const getStatusColor = (status: string) => {
    switch (status) {
      case 'Processing':
        return 'bg-warning/10 text-warning';
      case 'Shipped':
        return 'bg-primary-100 text-primary-600';
      case 'Delivered':
        return 'bg-success/10 text-success';
      case 'Cancelled':
        return 'bg-error/10 text-error';
      default:
        return 'bg-gray-100 text-gray-600';
    }
  };
  const timelineSteps = [{
    title: 'Order Placed',
    description: 'Your order has been confirmed',
    icon: <Package className="w-5 h-5" data-id="element-940" />,
    time: '10:00 AM',
    date: new Date(order.date).toLocaleDateString(),
    completed: true
  }, {
    title: 'Processing',
    description: 'Seller is preparing your package',
    icon: <Clock className="w-5 h-5" data-id="element-941" />,
    time: '11:30 AM',
    date: new Date(order.date).toLocaleDateString(),
    completed: ['Processing', 'Shipped', 'Delivered'].includes(order.status)
  }, {
    title: 'Picked Up',
    description: 'Package picked up by courier',
    icon: <Truck className="w-5 h-5" data-id="element-942" />,
    time: '02:15 PM',
    date: new Date(order.date).toLocaleDateString(),
    completed: ['Shipped', 'Delivered'].includes(order.status)
  }, {
    title: 'In Transit',
    description: 'Package is on the way to you',
    icon: <Map className="w-5 h-5" data-id="element-943" />,
    time: '04:45 PM',
    date: new Date(order.date).toLocaleDateString(),
    completed: ['Shipped', 'Delivered'].includes(order.status)
  }, {
    title: 'Out for Delivery',
    description: 'Driver is heading to your location',
    icon: <MapPin className="w-5 h-5" data-id="element-944" />,
    time: '08:30 AM',
    date: 'Today',
    completed: order.status === 'Delivered',
    active: order.status === 'Shipped'
  }, {
    title: 'Delivered',
    description: 'Package delivered successfully',
    icon: <CheckCircle2 className="w-5 h-5" data-id="element-945" />,
    time: '09:15 AM',
    date: 'Today',
    completed: order.status === 'Delivered'
  }];
  return <motion.div initial={{
    opacity: 0,
    x: 20
  }} animate={{
    opacity: 1,
    x: 0
  }} className="min-h-screen bg-gray-50 pb-32" data-id="element-946">
      <Header title="Track Order" data-id="element-947" />

      <div className="p-4 space-y-4" data-id="element-948">
        {/* Status Header */}
        <div className="bg-white p-4 rounded-2xl shadow-sm border border-gray-50 flex justify-between items-center" data-id="element-949">
          <div data-id="element-950">
            <p className="text-sm text-gray-500 mb-1" data-id="element-951">Estimated Delivery</p>
            <p className="font-bold text-gray-900 text-lg" data-id="element-952">
              {order.status === 'Delivered' ? 'Delivered' : 'Today, 09:00 AM - 12:00 PM'}
            </p>
          </div>
          <span className={`px-3 py-1.5 rounded-lg text-sm font-bold ${getStatusColor(order.status)}`} data-id="element-953">
            {order.status}
          </span>
        </div>

        {/* Map Placeholder */}
        <div className="bg-gradient-to-br from-primary-100 to-primary-50 h-48 rounded-2xl shadow-sm border border-primary-100 relative overflow-hidden flex flex-col items-center justify-center" data-id="element-954">
          <div className="absolute inset-0 opacity-20" style={{
          backgroundImage: 'radial-gradient(#14718f 1px, transparent 1px)',
          backgroundSize: '20px 20px'
        }} data-id="element-955"></div>

          {/* Dotted Path Illustration */}
          <div className="relative w-full max-w-[200px] h-20 flex items-center justify-between z-10" data-id="element-956">
            <div className="w-10 h-10 bg-white rounded-full shadow-md flex items-center justify-center z-10" data-id="element-957">
              <Package className="w-5 h-5 text-primary" data-id="element-958" />
            </div>

            <div className="flex-1 border-t-2 border-dashed border-primary-300 relative" data-id="element-959">
              {order.status === 'Shipped' && <motion.div animate={{
              x: [0, 100]
            }} transition={{
              repeat: Infinity,
              duration: 2,
              ease: 'linear'
            }} className="absolute -top-3 left-0 w-6 h-6 bg-primary rounded-full flex items-center justify-center shadow-lg" data-id="element-960">
                  <Truck className="w-3 h-3 text-white" data-id="element-961" />
                </motion.div>}
            </div>

            <div className="w-10 h-10 bg-white rounded-full shadow-md flex items-center justify-center z-10" data-id="element-962">
              <MapPin className="w-5 h-5 text-primary" data-id="element-963" />
            </div>
          </div>
          <p className="text-primary-800 font-medium text-sm mt-4 z-10 bg-white/50 px-3 py-1 rounded-full backdrop-blur-sm" data-id="element-964">
            {order.status === 'Delivered' ? 'Package arrived at destination' : 'Package is on the way'}
          </p>
        </div>

        {/* Delivery Info */}
        {['Shipped', 'Delivered'].includes(order.status) && <div className="bg-white p-4 rounded-2xl shadow-sm border border-gray-50 flex items-center justify-between" data-id="element-965">
            <div className="flex items-center gap-3" data-id="element-966">
              <div className="w-12 h-12 bg-gray-100 rounded-full overflow-hidden" data-id="element-967">
                <img src="https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?auto=format&fit=crop&q=80&w=100" alt="Driver" className="w-full h-full object-cover" data-id="element-968" />
              </div>
              <div data-id="element-969">
                <p className="font-bold text-gray-900" data-id="element-970">Adebayo M.</p>
                <p className="text-xs text-gray-500 flex items-center gap-1" data-id="element-971">
                  <Truck className="w-3 h-3" data-id="element-972" /> Motorcycle Courier
                </p>
              </div>
            </div>
            <div className="flex gap-2" data-id="element-973">
              <button className="w-10 h-10 rounded-full bg-primary/10 text-primary flex items-center justify-center hover:bg-primary/20 transition-colors" data-id="element-974">
                <MessageSquare className="w-5 h-5" data-id="element-975" />
              </button>
              <button className="w-10 h-10 rounded-full bg-primary/10 text-primary flex items-center justify-center hover:bg-primary/20 transition-colors" data-id="element-976">
                <Phone className="w-5 h-5" data-id="element-977" />
              </button>
            </div>
          </div>}

        {/* Timeline */}
        <div className="bg-white p-5 rounded-2xl shadow-sm border border-gray-50" data-id="element-978">
          <h3 className="font-bold text-gray-900 mb-6" data-id="element-979">Tracking History</h3>
          <div className="space-y-6 relative before:absolute before:inset-0 before:ml-5 before:-translate-x-px md:before:mx-auto md:before:translate-x-0 before:h-full before:w-0.5 before:bg-gradient-to-b before:from-transparent before:via-gray-200 before:to-transparent" data-id="element-980">
            {timelineSteps.map((step, idx) => <div key={idx} className="relative flex items-start gap-4" data-id="element-981">
                <div className={`w-10 h-10 rounded-full flex items-center justify-center shrink-0 z-10 ${step.completed ? 'bg-primary text-white shadow-md' : step.active ? 'bg-primary-100 text-primary border-2 border-primary shadow-sm' : 'bg-gray-100 text-gray-400'}`} data-id="element-982">
                  {step.icon}
                  {step.active && <span className="absolute w-full h-full rounded-full border-2 border-primary animate-ping opacity-75" data-id="element-983"></span>}
                </div>
                <div className="flex-1 pt-1" data-id="element-984">
                  <div className="flex justify-between items-start mb-1" data-id="element-985">
                    <h4 className={`font-bold text-sm ${step.completed || step.active ? 'text-gray-900' : 'text-gray-500'}`} data-id="element-986">
                      {step.title}
                    </h4>
                    <div className="text-right" data-id="element-987">
                      <p className="text-xs font-medium text-gray-900" data-id="element-988">
                        {step.time}
                      </p>
                      <p className="text-[10px] text-gray-500" data-id="element-989">{step.date}</p>
                    </div>
                  </div>
                  <p className="text-xs text-gray-500" data-id="element-990">{step.description}</p>
                </div>
              </div>)}
          </div>
        </div>
      </div>

      {/* Bottom Actions */}
      <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-gray-100 p-4 pb-safe z-40" data-id="element-991">
        <div className="max-w-md mx-auto flex gap-3" data-id="element-992">
          <Button variant="outline" className="flex-1" onClick={() => navigate(Screen.HELP_CENTER)} data-id="element-993">
            Contact Support
          </Button>
          <Button className="flex-1" onClick={() => navigate(Screen.ORDER_DETAIL, {
          orderId: order.id
        })} data-id="element-994">
            Order Details
          </Button>
        </div>
      </div>
    </motion.div>;
}